// Tests for this utility are covered by the Axios interceptor integration tests:
// src/restAPI/__tests__/index.interceptor.test.ts
import { APICallContextEnum } from 'types/api/APICallContextEnum';

export type APICallContext = {
  acSource: APICallContextEnum;
  acDashboardId?: string;
};

/**
 * Extracts the feature pathname from a hash-router URL by stripping:
 *   1. The leading '#'
 *   2. The tenant/BU prefix: /u/{tenant}/{businessUnit}
 *   3. Any query string or fragment
 *
 * Returns the feature-level path starting from the first segment after the BU,
 * e.g. '/dashboard/42', '/findings', '/dashboard/edit/5'.
 *
 * Returns '' if the hash doesn't match the expected /u/{tenant}/{bu}/... structure.
 *
 * Examples:
 *   '#/u/612/2689/dashboard/42?foo=bar'  → '/dashboard/42'
 *   '#/u/612/2689/findings'              → '/findings'
 *   '#/u/612/2689/findings/123'          → '/findings/123'
 */
function getHashPathname(hash: string): string {
  const withoutHash = hash.startsWith('#') ? hash.slice(1) : hash;
  // Strip query string and fragment
  const path = withoutHash.split('?')[0].split('#')[0];
  // Strip /u/{tenant}/{businessUnit} prefix — exactly 3 leading segments
  // Pattern: /u/<segment>/<segment><remainder>
  const match = path.match(/^\/u\/[^/]+\/[^/]+(\/.*)?$/);
  if (!match) return '';
  return match[1] ?? '';
}

/**
 * Derives the API call context from the current hash URL.
 * Used by the Axios request interceptor to inject x-ac-source / x-ac-dashboard-id headers.
 *
 * Matching priority (first match wins):
 *   1. /dashboard/create           → DASHBOARD_EDIT
 *   2. /dashboard/edit/{id}        → DASHBOARD_EDIT  (id must be > 0)
 *   3. /dashboard/{id}             → DASHBOARD       (id must be > 0; no further segments)
 *   4. /findings (any sub-path)    → FINDING_LIST
 *   5. anything else               → BLANK
 *
 * Rejects:
 *   - Dashboard ID 0 (maps to BLANK)
 *   - Non-numeric dashboard IDs (maps to BLANK)
 *   - Dashboard/findings patterns appearing only in query params or subpaths
 */
export function getAPICallContext(hash: string): APICallContext {
  const pathname = getHashPathname(hash);
  if (pathname.startsWith('/dashboard/create')) {
    return { acSource: APICallContextEnum.DASHBOARD_EDIT };
  }

  if (pathname.startsWith('/dashboard/edit/')) {
    return { acSource: APICallContextEnum.DASHBOARD_EDIT };
  }

  if (pathname.startsWith('/dashboard/')) {
    const afterDashboard = pathname
      .slice('/dashboard/'.length)
      .replace(/\/$/, '');
    // View mode: must be a single positive integer — no further path segments
    if (!afterDashboard.includes('/')) {
      const idNum = Number(afterDashboard);
      if (Number.isInteger(idNum) && idNum > 0) {
        return {
          acSource: APICallContextEnum.DASHBOARD,
          acDashboardId: afterDashboard,
        };
      }
    }
  }

  if (pathname === '/findings' || pathname.startsWith('/findings/')) {
    return { acSource: APICallContextEnum.FINDING_LIST };
  }

  return { acSource: APICallContextEnum.BLANK };
}
